
import React, { useState, useEffect } from 'react';
import Skeleton from 'react-loading-skeleton';
import {
  base64FileHeaderMapper,
  downloadBase64,
  getSharepointCdnFile
} from '../../utilities';
import { ApiService } from '../../constants';

interface ImageProps {
  src?: string | null;
  alt?: string | null;
  style?: React.CSSProperties;
  className?: string;
  placeholder?: string | null;
  onError?: React.ReactEventHandler<HTMLImageElement>;
}

const Image: React.FC<ImageProps> = ({
  src,
  alt = null,
  style,
  placeholder = null,
  onError
}) => {
  const [loading, setLoading] = useState(false);
  const [image, setImage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadImage = async () => {
      if (!src) {
        setImage(placeholder || null);
        setLoading(false);
        return;
      }

      const source = String(src);

      setLoading(true);
      setImage(null);

      // Path relatif, Blob URL, dan data URL langsung digunakan.
      if (
        source.startsWith('/') ||
        source.startsWith('./') ||
        source.startsWith('../') ||
        source.startsWith('blob:') ||
        source.startsWith('data:')
      ) {
        setImage(source);
        setLoading(false);
        return;
      }

      let url: URL;

      try {
        url = new URL(source);
      } catch {
        setImage(placeholder || null);
        setLoading(false);
        return;
      }

      let moodleHost = '';

      try {
        moodleHost = new URL(
          ApiService.imageConfig.isMoodle
        ).host;
      } catch {
        // Abaikan jika konfigurasi Moodle tidak valid.
      }

      // URL Moodle dan URL CDN langsung ditampilkan.
      if (
        url.host === moodleHost ||
        url.host === 'cdn-assets-beelajarlms.apps.binus.edu'
      ) {
        setImage(source);
        setLoading(false);
        return;
      }

      // SharePoint tetap menggunakan mekanisme lama.
      if (url.host === 'binusianorg.sharepoint.com') {
        try {
          const resp = await downloadBase64(source);

          if (cancelled) return;

          if (resp.data && resp.data.resultCode === 200) {
            setImage(
              base64FileHeaderMapper(resp.data.data)
            );
          } else {
            setImage(placeholder || null);
          }
        } catch (error) {
          console.error(
            'Failed to load SharePoint image:',
            error
          );

          if (!cancelled) {
            setImage(placeholder || null);
          }
        } finally {
          if (!cancelled) setLoading(false);
        }

        return;
      }

      // Pertahankan pemrosesan URL lain yang sudah ada.
      try {
        const cdnImage = getSharepointCdnFile(source);

        if (!cancelled) {
          setImage(cdnImage);
        }
      } catch (error) {
        console.error('Failed to process image URL:', error);

        if (!cancelled) {
          setImage(placeholder || null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadImage();

    return () => {
      cancelled = true;
    };
  }, [src, placeholder]);

  return (
    <>
      {loading ? (
        <Skeleton style={style} height={100} />
      ) : (
        <img
          referrerPolicy="unsafe-url"
          src={image || placeholder || undefined}
          style={style}
          alt={alt || ''}
          onError={onError}
        />
      )}
    </>
  );
};

export default Image;
